import Service from '@/services/weave-x-service';
import { VisualizationHtml } from '@/common/components/wavvest-planning/types';

type FileType = {
  fileName: string;
  fileContent: string;
};

interface UploadContextFileRequest {
  userId: string;
  sessionId: string;
  files: FileType[];
}

type ReattachContextFilesRequest = {
  userId: string;
  sourceSessionId: string;
  targetSessionId: string;
  fileKeys: string[];
};

export type ReattachContextFilesResponse = {
  reattachedFileKeys: Array<{
    fileKey: string;
    status: 'success' | 'failed';
  }>;
};

export const uploadContextFile = async (request: UploadContextFileRequest): Promise<boolean> => {
  const response = await Service.post('/api/visualization/files/upload', request);
  return response === null;
};

export const reattachContextFiles = async (
  request: ReattachContextFilesRequest,
): Promise<ReattachContextFilesResponse> => {
  return await Service.put('/api/visualization/files/reattach', request);
};

export const deleteContextFile = async (fileKey: string): Promise<boolean> => {
  const response = await Service.delete('/api/visualization/files/delete', {
    data: {
      fileKey,
    },
  });
  return response === null;
};

export interface PlaidData {
  accounts: Array<{
    user_id: string;
    account_id: string;
    account_name: string;
    account_type: string;
    available_balance: number;
    current_balance: number;
    [key: string]: unknown;
  }>;
  summary: {
    total_assets: number;
    total_liabilities: number;
    total_investments: number;
    net_worth: number;
    [key: string]: unknown;
  };
  spendings: {
    [key: string]: unknown;
  };
  asset_allocation: {
    [key: string]: unknown;
  };
  debt_breakdown: {
    credit?: number;
    mortgage?: number;
    [key: string]: unknown;
  };
  securities: [
    {
      [key: string]: unknown;
    },
  ];
  recent_transactions: [
    {
      [key: string]: unknown;
    },
  ];
}

export const getPlaidAggregatedData = async (investorId: string): Promise<PlaidData | null> => {
  return await Service.get(`/api/plaidcash/${investorId}`);
};

interface ShareCanvasRequest {
  userId: string;
  sessionId: string;
  title: string;
  visualizationId: string;
  visualizationHtml: VisualizationHtml;
  sharedWith: string[];
}

export const shareCanvas = async (request: ShareCanvasRequest): Promise<boolean> => {
  const response = await Service.post('/api/visualization/share', request);
  return response === null;
};

export interface OrganizationTemplateItem {
  clientId: string;
  templateName: string;
  templateDescription: string;
  templateSourceCode: string;
  createdBy: string;
  //Request body does not have below fields, but response body has these fields as these are generated by the server
  createdAt?: number;
  templateId?: string;
}

export const saveOrganizationTemplate = async (
  request: OrganizationTemplateItem,
): Promise<boolean> => {
  const response = await Service.post('/api/templates/create', request);
  return response === null;
};

export interface OrganizationTemplateListResponse {
  templates: Array<OrganizationTemplateItem>;
}

export const getOrganizationTemplatesPaginated = async (
  clientId: string,
  nextToken: string,
  limit: number,
): Promise<{
  templates: Array<OrganizationTemplateItem>;
  hasMore: boolean;
  nextToken: string;
}> => {
  return await Service.get(`/api/templates/list/${clientId}/paginated`, {
    params: {
      limit,
      nextToken,
    },
  });
};

export const deleteOrganizationTemplate = async (
  clientId: string,
  templateId: string,
): Promise<boolean> => {
  const response = await Service.delete(`/api/templates/delete/${clientId}/${templateId}`);
  return response === null;
};

export type PublisherInfo = {
  riaName: string;
  clientId: string;
  advisorId: string;
  advisorName: string;
  advisorEmail: string;
};

export type PublishCommunityTemplateRequest = {
  templateName: string;
  templateDescription: string;
  templateSourceCode: string;
  tags?: string[];
  publisherInfo?: PublisherInfo;
};

export const publishCommunityTemplate = async (
  request: PublishCommunityTemplateRequest,
): Promise<void> => {
  await Service.post('/api/templates/publish', request);
};

export interface CommunityTemplateItem {
  templateId: string;
  displayName: string;
  displayDescription: string;
  templateSourceCode: string;
  createdAt: number;
  updatedAt: number;
  allNames: string[];
  allDescriptions: string[];
  usageCount: number;
  tags: string[];
  originalPublisher: PublisherInfo;
  publishers: Array<PublisherInfo>;
  publisherCount: number;
}

export const ListCommunityTemplates = async (filterCondition: {
  tags?: string[];
  sortBy?: string;
  limit?: number;
  cursor?: string;
  riaClientId?: string;
}): Promise<{
  templates: Array<CommunityTemplateItem>;
  total: number;
  hasMore: boolean;
}> => {
  return await Service.get('/api/templates/communities/list', {
    params: filterCondition,
  });
};

export const recordTemplateUsage = async (
  templateId: string,
): Promise<{ success: boolean; newUsageCount: number }> => {
  return await Service.post(`/api/templates/communities/${templateId}/use`);
};

export const patchCommunityTemplate = async (
  templateId: string,
  reqBody: {
    templateName?: string;
    templateDescription?: string;
    tags?: string[];
  },
): Promise<void> => {
  await Service.patch(`/api/templates/communities/${templateId}`, {
    data: reqBody,
  });
};

export const deleteCommunityTemplateById = async (templateId: string): Promise<void> => {
  await Service.delete(`/api/templates/communities/${templateId}`);
};

export type CreateArtifactResponse = {
  success: boolean;
  link: string;
  artifactId: string;
  createdAt: string;
};

export const createArtifact = async (payload: {
  session_id: string;
  htmlSourceCode: string;
}): Promise<CreateArtifactResponse> => {
  return await Service.post('/api/artifacts/', payload);
};

type GetApexAccountDataResponse = {
  accounts: Array<{
    goal: string;
    buyingPower: string;
    openBalance: string;
    totalEquity: string;
    advisorArchGoal: {
      kind: string;
      risk_score: number;
      time_horizon: number;
    };
  }>;
  errors: Array<[string, string]>;
};

export const getApexAccountData = async (
  correspondentId: string,
  investorId: string,
): Promise<GetApexAccountDataResponse> => {
  return await Service.get('/api/apex/accounts', {
    params: {
      correspondentId,
      customerId: investorId,
    },
  });
};

type GetApexAccountOverviewResponse = {
  accounts: Array<{
    accountId: string;
    accountName: string;
    accountNumber: string;
    goal: string;
    totalEquity: string;
    totalEquityDate: string;
  }>;
};

export const getApexAccountOverview = async (
  userId: string,
): Promise<GetApexAccountOverviewResponse> => {
  return await Service.get('/api/apex/accounts-overview', {
    params: {
      userId,
    },
  });
};

type GetAssetsAllocationResponse = {
  kind: string;
  riskScore: number;
  timeHorizonYears: number;
};

export const getAssetsAllocation = async (
  correspondentId: string,
  accountId: string,
): Promise<GetAssetsAllocationResponse> => {
  return await Service.get('/api/apex/asset-allocation', {
    params: {
      correspondentId,
      accountId,
    },
  });
};

export type GetInvestorKycResultsResponse = {
  clientUserId: string;
  id: string;
  user: {
    dateOfBirth: string;
    emailAddress: string | null;
    name: {
      givenName: string;
      familyName: string;
    };
    phoneNumber: string;
    address: {
      street: string;
      street2: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
    };
  };
  status: string;
};

export const getInvestorKycResults = async (
  investorId: string,
): Promise<GetInvestorKycResultsResponse> => {
  return await Service.get(`/api/kyc/${investorId}/results`);
};

export type GetExistingPlanInfoResponse = {
  plans: Array<{
    id: string;
    title: string;
    updatedAt: string;
  }>;
  lastPlanDate: string;
};

export const getExistingPlanInfo = async (
  investorId: string,
): Promise<GetExistingPlanInfoResponse> => {
  return await Service.get(`/api/plans/info`, {
    params: {
      customerId: investorId,
    },
  });
};

export type GetAllFilehubFilesForInvestorResponse = Array<{
  s3Key: string;
  fileName: string;
  mimeType: string;
  sizeByte: number;
  uploadAt: string;
  tags: Array<string>;
  description: string;
}>;

export const getAllFilehubFilesForInvestor = async (
  investorId: string,
): Promise<GetAllFilehubFilesForInvestorResponse> => {
  return await Service.get(`/api/filehub/customers/shared-files`, {
    params: {
      customerId: investorId,
    },
  });
};

export type CopyFileHubFileToContextFileResponse = {
  addedFileKeys: Array<{
    fileKey: string;
    status: 'success' | 'failed';
  }>;
};

export const copyFileHubFileToContextFile = async (
  userId: string,
  sessionId: string,
  s3Keys: string[],
): Promise<CopyFileHubFileToContextFileResponse> => {
  return await Service.put(`/api/visualization/files/add-from-filehub`, {
    data: {
      userId,
      sessionId,
      s3Keys,
    },
  });
};

export type ListUsableTemplatesForGuidedModeResponse = Array<{
  templateId: string;
  displayName: string;
  displayDescription: string;
  templateSourceCode: string;
}>;

export const listUsableTemplatesForGuidedMode =
  async (): Promise<ListUsableTemplatesForGuidedModeResponse> => {
    return await Service.get('/api/templates/communities/list/usable-for-guided-mode');
  };

export type GeneratePdfResponse = {
  downloadablePdfUrl: string;
  filename: string;
  renderTimeMs: number;
  metadata: {
    createdAt: string;
    s3Filename: string;
    s3Key: string;
    presignedUrlExpiryTime: string;
    fileSize: number;
    fileSizeHuman: string;
  };
};

export type GeneratePdfRequestBody = {
  htmlSourceCodeBase64: string;
  pageSize?: 'single-page' | 'A4' | 'A3' | 'us-letter' | 'legal' | 'tabloid';
  unit?: 'mm' | 'inch';
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  filename?: string;
  urlExpirationSeconds?: number;
};

export const generatePdf = async (
  payload: GeneratePdfRequestBody,
): Promise<GeneratePdfResponse> => {
  return await Service.post('/api/pdf/generate', payload);
};

type CreateSessionAssociationPayload = {
  investorId: string;
  cognitoId?: string;
  threadId: string;
  title: string;
  description: string;
  notes: string;
};

type CreateSessionAssociationResponse = {
  assignmentId: string;
  investorId: string;
  threadId: string;
  advisorId: string;
  title: string;
  description: string;
  notes: string;
  createdAt: number;
  updatedAt: number;
};

export const createSessionAssociation = async (
  payload: CreateSessionAssociationPayload,
): Promise<CreateSessionAssociationResponse> => {
  return await Service.post('/api/session-associations', payload);
};

export const getSessionAssociationByAssignmentId = async (
  assignmentId: string,
): Promise<CreateSessionAssociationResponse> => {
  return await Service.get(`/api/session-associations/${assignmentId}`);
};

export const updateSessionAssociationRecord = async (
  assignmentId: string,
  payload: {
    title?: string;
    description?: string;
    notes?: string;
    cognitoId?: string;
  },
): Promise<CreateSessionAssociationResponse> => {
  return await Service.patch(`/api/session-associations/${assignmentId}`, payload);
};

export const deleteSessionAssociationRecord = async (assignmentId: string): Promise<void> => {
  return await Service.delete(`/api/session-associations/${assignmentId}`);
};

export type GetSessionAssociationByInvestorIdResponse = {
  items: Array<CreateSessionAssociationResponse>;
  nextToken: string;
  count: number;
};
export const getSessionAssociationByInvestorId = async (
  investorId: string,
  limit: number = 20,
  nextToken: string = '',
): Promise<GetSessionAssociationByInvestorIdResponse> => {
  return await Service.get(`/api/session-associations/by-investor/${investorId}`, {
    params: {
      limit,
      nextToken,
    },
  });
};

export type GetSessionAssociationByAdvisorIdResponse = GetSessionAssociationByInvestorIdResponse;

export const getSessionAssociationByAdvisorId = async (
  advisorId: string,
  limit: number = 20,
  nextToken: string = '',
): Promise<GetSessionAssociationByAdvisorIdResponse> => {
  return await Service.get(`/api/session-associations/by-advisor/${advisorId}`, {
    params: {
      limit,
      nextToken,
    },
  });
};

export const getSessionAssociationByThreadId = async (
  threadId: string,
): Promise<CreateSessionAssociationResponse | null> => {
  return await Service.get(`/api/session-associations/by-thread/${threadId}`);
};

export interface GuidanceConfigPrompt {
  key: string;
  name: string;
}

export interface GuidanceConfigCategory {
  key: string;
  name: string;
  instruction: string;
  iconSvg?: string;
  suggestedPrompts: GuidanceConfigPrompt[];
}

export const getGuidanceConfig = async (): Promise<GuidanceConfigCategory[]> => {
  return await Service.get('/api/guidance/config');
};
